import { LocalizedText } from '@mews/framework';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { ResourceState } from '@mews-commander/data-access';
import { Stack } from '@optimus-web/core';
import {
    getCurrentEnterpriseReservationPersonCount,
    getCurrentEnterpriseReservationServiceId,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector } from '@mews-commander/utils';
import { ConnectedEditDialogForm } from '@mews-commander/legacy-form';
import preferences from '../../../preferences';
import { useFetchKeyCuttersQuery } from '../../queries/useFetchKeyCuttersQuery';
import CheckInForm from './CheckInForm';
import { getCurrentEnterpriseReservationAssignedResourceState } from './selectors';

const {
    selectors: { getPreferredKeyCutterId },
} = preferences;

const reservationCheckInFormName = 'smart-detail-reservation-check-in-form';

interface ReservationCheckInDialogProps {
    active: boolean;
    reservationId: string;
    closeDialog: () => void;
    onSuccess: () => void;
    assignedResourceId: string;
}

const ReservationCheckInDialog = ({
    assignedResourceId,
    onSuccess,
    active,
    reservationId,
    closeDialog,
}: ReservationCheckInDialogProps) => {
    const { data: keyCutters = [] } = useFetchKeyCuttersQuery();

    const keyCutterId = useCommanderSelector(getPreferredKeyCutterId);
    const showKeyCutterCard = keyCutters.length > 0;

    const personCount = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationPersonCount(state, { id: reservationId })
    );
    const serviceId = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationServiceId(state, { id: reservationId })
    );
    const resourceState = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationAssignedResourceState(state, { id: reservationId })
    );

    const isInspected = resourceState === ResourceState.Inspected;

    if (!serviceId) {
        return null;
    }

    return (
        <ConnectedEditDialogForm
            formName={reservationCheckInFormName}
            active={active}
            primaryButtonTextKey={TextKey.CheckIn}
            primaryButtonDataTestId={DataTestId.CheckInButton}
            closeDialog={closeDialog}
            title={<LocalizedText textKey={TextKey.CheckIn} />}
        >
            <Stack vertical spacing="100">
                <CheckInForm
                    reservationId={reservationId}
                    assignedResourceId={assignedResourceId}
                    serviceId={serviceId}
                    isInspected={isInspected}
                    form={reservationCheckInFormName}
                    onSuccess={onSuccess}
                    initialValues={{
                        createKeys: showKeyCutterCard,
                        keyCount: personCount,
                        keyCutter: keyCutterId,
                    }}
                />
            </Stack>
        </ConnectedEditDialogForm>
    );
};

export default ReservationCheckInDialog;
